import { useMutation, useQuery } from '@apollo/client';
import { Theme } from '@emotion/react';
import { fakeMeetings } from '../../../demo-data';
import { useDemoMode } from '../../../hooks';
import { GetAllMeetingsResponse } from '../../../types';
import { extractMeetingLocation, formatTimeToAM } from '@repo-sapiens/helpers';
import {
  ALL_MEETINGS,
  CANCEL_SCHEDULED_BOT,
  RESCHEDULE_SCHEDULED_BOT,
  SCHEDULED_BOTS,
} from '@repo-sapiens/graphql';
import {
  AttendeeInfo,
  E_TransactionStatus,
} from '@repo-sapiens/data-access-models';
import { E_CalendarEventType, E_Services } from '@repo-sapiens/constants';

type ScheduledBot = {
  id: string;
  meetingId: string;
  botId: string;
  status: E_TransactionStatus;
};

type MeetingList = {
  id: string;
  title: string;
  label?: string;
  labelBgColor?: keyof Theme['colors']['text'];
  type: 'meeting' | 'email';
  meetingId?: string;
  start?: number;
  end?: number;
  startAt?: string;
  endAt?: string;
  attendees?: {
    userName: string;
    source: string;
  }[];
  location?: string;
} & ScheduledBot;

export const useMeeting = (dateSelected?: number, endDateSelected?: number) => {
  const { demoMode } = useDemoMode();
  //const {dateSelected} = useContext(SystemContext);

  const { data: meetingData, loading } = useQuery<GetAllMeetingsResponse>(
    ALL_MEETINGS,
    {
      //fetchPolicy: 'network-only',
      //pollInterval: demoMode ? undefined : 5000,
      variables: {
        date: dateSelected,
        endDate: endDateSelected,
      },
    }
  );

  const {
    data: botData,
    loading: botLoading,
    refetch: refetchBot,
  } = useQuery<{
    scheduledBots: ScheduledBot[];
  }>(SCHEDULED_BOTS);

  const [cancelBot] = useMutation(CANCEL_SCHEDULED_BOT);
  const [rescheduleBot] = useMutation(RESCHEDULE_SCHEDULED_BOT);

  const cancelScheduledBot = async (botId: string) => {
    try {
      await cancelBot({
        variables: { botId },
        //refetchQueries: [{ query: SCHEDULED_BOTS }],
      });
      //refetch();
      refetchBot();
    } catch (error) {
      console.error('Error cancelling scheduled bot:', error);
    }
  };

  const rescheduleScheduledBot = async (meetingId: string) => {
    try {
      await rescheduleBot({
        variables: { meetingId },
        //refetchQueries: [{ query: SCHEDULED_BOTS }],
      });
      refetchBot();
      //refetch();
    } catch (error) {
      console.error('Error rescheduling scheduled bot:', error);
    }
  };

  const externalMeetings = demoMode
    ? fakeMeetings
    : meetingData?.allMeetings?.externals || [];

  const classifications = meetingData?.allMeetings?.eventClassification;

  const allMeetings = externalMeetings
    .map((item, idx) => {
      const bot = botData?.scheduledBots?.find(
        (bot) => bot.meetingId === item.meetingId
      );
      return {
        id: item._id || item.meetingId || (idx + 2).toString(),
        title: `${item.meetingName}`,
        label:
          classifications?.find((c) => c.id === item.meetingId)?.eventType ||
          'External',
        labelBgColor: 'highlight/1' as keyof Theme['colors']['text'],
        type: 'meeting',
        meetingId: item.meetingId,
        start: new Date(item.startAt).getTime(),
        end: new Date(item.endAt).getTime(),
        startAt: formatTimeToAM(new Date(item.startAt)),
        endAt: formatTimeToAM(new Date(item.endAt)),
        attendees: item.attendees.map((attendee: AttendeeInfo) => ({
          userName: attendee.user_full_name,
          source: attendee.profile_pic_url,
        })),
        location: extractMeetingLocation(item.location),
        botId: bot?.botId || '',
        status: bot?.status,
      } as MeetingList;
    })
    .concat(
      (meetingData?.allMeetings?.internals || [])?.map((item, idx) => {
        const bot = botData?.scheduledBots?.find(
          (bot) => bot.meetingId === item.id
        );

        const eventType = classifications?.find(
          (c) => c.id === item.id
        )?.eventType;
        return {
          id: item.id || (idx + 2).toString(),
          title: `${item.eventTitle}`,
          label: eventType
            ? eventType
            : (item.attendees?.length || 0) > 0
            ? 'Internal'
            : 'Blocked time',
          labelBgColor: 'seconday' as keyof Theme['colors']['text'],
          type: 'meeting',
          meetingId: item.id,
          start: new Date(item.start).getTime(),
          end: new Date(item.end).getTime(),
          startAt: formatTimeToAM(new Date(item.start)),
          endAt: formatTimeToAM(new Date(item.end)),
          attendees: item.attendees?.map((attendee) => ({
            userName: attendee.displayName || attendee.email,
            source: '',
          })),
          location: extractMeetingLocation(item.location || ''),
          botId: bot?.botId || '',
          status: bot?.status,
        } as MeetingList;
      })
    )
    .sort((a, b) => (a.start || 0) - (b.start || 0)) as MeetingList[];

  return {
    loading: loading || botLoading,
    allMeetings,
    externalMeetings,
    cancelScheduledBot,
    rescheduleScheduledBot,
  };
};
